import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { diffLines, type Change } from "diff";
import { Check, GitMerge, Loader2, RotateCcw, X } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DocumentSection, FactSheet } from "@/api/schemas";
import { regenerateSection } from "@/api/client";
import { useDocgenStore } from "./useDocgenStore";

/**
 * Sprint B — W5.
 *
 * Side-by-side diff for the regenerate-section flow. Replaces the
 * Ring 2B W6 "instant replace" behaviour where the regen mutation
 * silently overwrote the section content the moment the OpenAI call
 * returned — architects had no chance to compare the two versions
 * before committing.
 *
 * The sheet owns the regen mutation itself (so the parent ``DocSectionCard``
 * doesn't double-request) and surfaces three exits:
 *
 * * ``Prihvati`` — patches the section in the docgen store with the
 *   regenerated content + ``llm_refined`` source tag, then closes.
 * * ``Odbaci`` — closes without touching the section. Mutation result
 *   is discarded.
 * * ``Spoji ručno`` — closes the diff and opens the existing
 *   ``RefineSheet`` pre-loaded with both texts in the system message
 *   (via ``initialInstruction``) so the architect can hand-merge.
 */

interface RegenerateDiffSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: DocumentSection;
  factSheet: FactSheet;
  /**
   * Called when the user picks "Spoji ručno". Parent is responsible
   * for opening the ``RefineSheet`` with the proposed content
   * pre-staged. The diff sheet closes itself before invoking.
   */
  onMergeManually: (proposedContent: string) => void;
}

export function RegenerateDiffSheet({
  open,
  onOpenChange,
  section,
  factSheet,
  onMergeManually,
}: RegenerateDiffSheetProps) {
  const patchSection = useDocgenStore((s) => s.patchSection);
  const [regeneratedContent, setRegeneratedContent] = useState<string | null>(
    null,
  );

  const mutation = useMutation({
    mutationFn: () =>
      regenerateSection({ section_id: section.section_id, fact_sheet: factSheet }),
    onSuccess: (data) => {
      if (data.status === "ok" && data.section?.content) {
        setRegeneratedContent(data.section.content);
      }
    },
  });

  // Kick off the regen the first time the sheet opens (and again if the
  // user clicks "Pokušaj ponovno"). We don't auto-rerun on every open
  // because the user may close and reopen to glance at the diff.
  useEffect(() => {
    if (open && regeneratedContent === null && !mutation.isPending && !mutation.error) {
      mutation.mutate();
    }
    if (!open) {
      // Drop stale state so the next open re-runs on a fresh slate.
      setRegeneratedContent(null);
      mutation.reset();
    }
    // mutation reference is stable per render-pair; intentionally narrow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const diffRows = useMemo(() => {
    if (regeneratedContent == null) return null;
    return computeDiffRows(section.content, regeneratedContent);
  }, [section.content, regeneratedContent]);

  function handleAccept() {
    if (!regeneratedContent) return;
    patchSection(section.section_id, {
      content: regeneratedContent,
      source: "llm_refined",
    });
    onOpenChange(false);
  }

  function handleMerge() {
    if (!regeneratedContent) return;
    onOpenChange(false);
    onMergeManually(regeneratedContent);
  }

  function handleRetry() {
    setRegeneratedContent(null);
    mutation.reset();
    mutation.mutate();
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-[1100px] flex-col sm:max-w-[1100px]"
      >
        <SheetHeader>
          <SheetTitle>Regeneracija — {section.title}</SheetTitle>
          <SheetDescription>
            Lijevo: trenutni tekst. Desno: novi prijedlog. Crveno = uklonjeno,
            zeleno = dodano. Pregledaj prije nego što prihvatiš.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-5">
          {mutation.isPending && (
            <div className="flex flex-1 items-center justify-center gap-2 text-stone-500">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              <span className="text-sm">Regeneriram sekciju...</span>
            </div>
          )}

          {mutation.error instanceof Error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.85rem] text-red-700">
              <div className="font-medium">Greška pri regeneraciji</div>
              <div className="mt-1 text-[0.8rem] text-red-600">
                {mutation.error.message}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={handleRetry}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Pokušaj ponovno
              </Button>
            </div>
          )}

          {!mutation.isPending && diffRows && (
            <DiffPanes
              currentContent={section.content}
              proposedContent={regeneratedContent ?? ""}
              rows={diffRows}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-stone-100 p-4">
          <div className="text-[0.78rem] text-stone-500">
            {diffRows && (
              <>
                <span className="font-medium text-stone-700">
                  {diffRows.added} dodano
                </span>
                {" · "}
                <span className="font-medium text-stone-700">
                  {diffRows.removed} uklonjeno
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
              Odbaci
            </Button>
            <Button
              variant="outline"
              onClick={handleMerge}
              disabled={!regeneratedContent}
              title="Otvori AI doradu s oba teksta vidljiva"
            >
              <GitMerge className="h-4 w-4" />
              Spoji ručno
            </Button>
            <Button onClick={handleAccept} disabled={!regeneratedContent}>
              <Check className="h-4 w-4" />
              Prihvati
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ---- Diff rendering -------------------------------------------------------

export interface DiffSummary {
  changes: Change[];
  added: number;
  removed: number;
}

export function computeDiffRows(current: string, proposed: string): DiffSummary {
  // Line-level diff is the right granularity for prose paragraphs:
  // word-level here is too noisy for the 6-12 sentence sections the
  // assembler emits. Architects can spot semantic drift at a glance
  // when the unit is "one sentence per row".
  const changes = diffLines(current, proposed, { newlineIsToken: false });
  let added = 0;
  let removed = 0;
  for (const c of changes) {
    const lineCount = (c.value.match(/\n/g)?.length ?? 0) + (c.value.endsWith("\n") ? 0 : 1);
    if (c.added) added += lineCount;
    else if (c.removed) removed += lineCount;
  }
  return { changes, added, removed };
}

interface DiffPanesProps {
  currentContent: string;
  proposedContent: string;
  rows: DiffSummary;
}

export interface AlignedRow {
  /** Line text on the "current" side, or ``null`` for blank padding. */
  left: string | null;
  /** Line text on the "proposed" side, or ``null`` for blank padding. */
  right: string | null;
  kind: "equal" | "added" | "removed" | "changed";
}

function DiffPanes({ currentContent, proposedContent: _proposedContent, rows }: DiffPanesProps) {
  const aligned = useMemo(() => alignSides(rows.changes), [rows.changes]);

  // Empty diff (regen returned identical content) — show a calm message
  // instead of two empty panes that look like a load failure.
  if (rows.added === 0 && rows.removed === 0) {
    return (
      <div className="flex flex-1 flex-col gap-3 overflow-hidden">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[0.82rem] text-amber-800">
          AI je vratio identičan tekst — nema razlika za prikaz.
          Možeš odbaciti ili pokušati ponovno.
        </div>
        <DiffPaneStatic
          title="Trenutni tekst"
          subtitle={`${currentContent.split("\n").length} linija`}
          content={currentContent}
        />
      </div>
    );
  }

  return (
    <div className="grid flex-1 grid-cols-2 gap-3 overflow-hidden">
      <DiffPane
        title="Trenutni tekst"
        side="left"
        rows={aligned}
        emptyHint="(uklonjeno)"
      />
      <DiffPane
        title="Novi prijedlog"
        side="right"
        rows={aligned}
        emptyHint="(dodano)"
      />
    </div>
  );
}

export function alignSides(changes: Change[]): AlignedRow[] {
  // Re-shape ``diff``'s sequential change list into two parallel
  // columns: lines that exist on both sides line up, lines unique to
  // one side get a ``null`` placeholder on the other so visual scrolling
  // stays in sync.
  const out: AlignedRow[] = [];
  let i = 0;
  while (i < changes.length) {
    const cur = changes[i];
    const lines = splitLines(cur.value);

    if (!cur.added && !cur.removed) {
      for (const line of lines) {
        out.push({ left: line, right: line, kind: "equal" });
      }
      i += 1;
      continue;
    }

    // Look-ahead: a remove immediately followed by an add is the
    // "modified line" pattern — pair them up so the architect can read
    // the change inline.
    if (cur.removed && changes[i + 1]?.added) {
      const removed = splitLines(cur.value);
      const added = splitLines(changes[i + 1].value);
      const len = Math.max(removed.length, added.length);
      for (let k = 0; k < len; k++) {
        out.push({
          left: removed[k] ?? null,
          right: added[k] ?? null,
          kind: "changed",
        });
      }
      i += 2;
      continue;
    }

    if (cur.removed) {
      for (const line of lines) {
        out.push({ left: line, right: null, kind: "removed" });
      }
      i += 1;
      continue;
    }

    // ``cur.added`` standalone (no preceding remove)
    for (const line of lines) {
      out.push({ left: null, right: line, kind: "added" });
    }
    i += 1;
  }
  return out;
}

function splitLines(value: string): string[] {
  // Drop the trailing empty string that ``"a\n".split("\n")`` produces;
  // the diff package emits chunks with a trailing newline by default.
  const lines = value.split("\n");
  if (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  return lines;
}

interface DiffPaneProps {
  title: string;
  side: "left" | "right";
  rows: AlignedRow[];
  emptyHint: string;
}

function DiffPane({ title, side, rows, emptyHint }: DiffPaneProps) {
  return (
    <section className="flex min-h-0 flex-col rounded-md border border-stone-200 bg-white">
      <header className="flex items-center justify-between border-b border-stone-100 px-3 py-2">
        <span className="text-[0.78rem] font-bold uppercase tracking-[0.05em] text-stone-500">
          {title}
        </span>
      </header>
      <div className="flex-1 overflow-auto font-mono text-[0.78rem] leading-relaxed">
        {rows.map((row, idx) => {
          const value = side === "left" ? row.left : row.right;
          const isPresent = value !== null;
          const tone = pickTone(row.kind, side, isPresent);
          return (
            <div
              key={idx}
              className={cn(
                "flex border-b border-stone-50 px-3 py-1 last:border-b-0 whitespace-pre-wrap",
                tone,
              )}
            >
              <span className="mr-3 inline-block w-7 select-none text-right text-stone-300">
                {idx + 1}
              </span>
              <span className="flex-1 break-words">
                {isPresent ? value || "\u00A0" : (
                  <span className="text-stone-300">{emptyHint}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function pickTone(
  kind: AlignedRow["kind"],
  side: "left" | "right",
  isPresent: boolean,
): string {
  if (!isPresent) return "bg-stone-50/40 text-stone-300";
  if (kind === "equal") return "text-stone-700";
  if (kind === "removed" && side === "left") return "bg-red-50 text-red-900";
  if (kind === "added" && side === "right") return "bg-emerald-50 text-emerald-900";
  if (kind === "changed") {
    return side === "left"
      ? "bg-red-50/70 text-red-900"
      : "bg-emerald-50/70 text-emerald-900";
  }
  // E.g. an "added" row viewed from the left side (placeholder column).
  return "bg-stone-50/40 text-stone-400";
}

interface DiffPaneStaticProps {
  title: string;
  subtitle: string;
  content: string;
}

function DiffPaneStatic({ title, subtitle, content }: DiffPaneStaticProps) {
  return (
    <section className="flex min-h-0 flex-1 flex-col rounded-md border border-stone-200 bg-white">
      <header className="flex items-center justify-between border-b border-stone-100 px-3 py-2">
        <span className="text-[0.78rem] font-bold uppercase tracking-[0.05em] text-stone-500">
          {title}
        </span>
        <span className="text-[0.72rem] text-stone-400">{subtitle}</span>
      </header>
      <pre className="flex-1 overflow-auto whitespace-pre-wrap p-3 font-mono text-[0.78rem] leading-relaxed text-stone-700">
        {content}
      </pre>
    </section>
  );
}
